import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import ErrorActions from 'sharedreact/actions/ErrorActions';
import askConfirmation from 'sharedreact/components/askConfirmation';
import { FilterMode } from 'sharedreact/constants/generic/Constants';
import Filter from 'sharedreact/data/Filter';
import UploadedFile from 'sharedreact/data/UploadedFile';
import SettingsStore from 'sharedreact/stores/SettingsStore';
import { translate } from 'sharedreact/utils/LanguageSupport';
import { assertNonNullish } from 'sharedreact/utils/TypeSupport';

import FontFile from 'workspaces/launcher/data/FontFile';
import FontsActions from 'workspaces/launcher/actions/FontsActions';
import FontsTab from 'workspaces/launcher/components/admin/FontsTab';
import FontsStore from 'workspaces/launcher/stores/FontsStore';
import asFunctional from 'sharedreact/components/asFunctional';

type Props = {
  index: number,
  activeTab: number
};

type State = ConstructorParameters<typeof FontsTab>[0];

function getStores () {
  return [
    SettingsStore,
    FontsStore
  ];
}

function handleSetFilter (filterString: string): void {
  FontsActions.setFilter(
    FilterMode.GALLERY,
    Filter.createFromString(filterString, FontsStore.getFilterKeywords()),
    filterString);

  FontsActions.activateFilter(FilterMode.GALLERY);
}

const FONT_EXTENSIONS = [
  '.otf', '.otc', '.ttf', '.ttc',
  '.pfb', '.pfm', '.afm', '.pfa',
  '.cmap', '.map', '.fnt'
];

function _isValidExtension (filename: string): boolean {
  const dotPos: number = filename.lastIndexOf('.');
  if (dotPos !== -1) {
    const extension = filename.substring(dotPos).toLowerCase();

    return FONT_EXTENSIONS.includes(extension);
  }

  return false;
}

function _notIsValidExtension (filename: string): boolean {
  return !_isValidExtension(filename);
}

const FONT_EXTENSIONS_STRING = FONT_EXTENSIONS.join(',');

function handleDropFiles (files: FileList | File[]): void {
  const filesArray: File[] = [...files];

  if (filesArray.map(file => file.name).findIndex(_notIsValidExtension) === -1) {
    askConfirmation(
      'ae_confirm_uploadfont',
      translate('launcher.admin.fonts.confirmupload.title'),
      translate('launcher.admin.fonts.confirmupload.message'),
      undefined, undefined,
      translate('launcher.admin.fonts.confirmupload.button.agree'),
      translate('launcher.admin.fonts.confirmupload.button.decline')
    ).then(() => FontsActions.uploadFonts(filesArray), () => {});
  } else {
    setTimeout(() =>
      ErrorActions.setError(
        'launcher.admin.fonts.err.msg.invalidfontfiles', [],
        'launcher.admin.fonts.err.info.invalidfontfiles', []
      )
    );
  }
}

function handleRemove (id: string): void {
  handleRemoveMulti(Immutable.Set([id]));
}

function handleRemoveMulti (ids: Immutable.Set<string>): void {
  if (ids.size === 1) {
    let fontName: string = '';
    const font: FontFile | undefined = FontsStore.getItem(ids.first());
    if (font != null) {
      fontName = font.name;
    }

    askConfirmation(
      'ae_confirm_removefont',
      translate('launcher.admin.fonts.removeitem.title'),
      translate('launcher.admin.fonts.removeitem.message', fontName)
    ).then(() => FontsActions.remove(ids), () => {});
  } else {
    askConfirmation(
      'ae_confirm_removefont',
      translate('launcher.admin.fonts.removeitems.title'),
      translate('launcher.admin.fonts.removeitems.message', ids.size)
    ).then(() => FontsActions.remove(ids), () => {});
  }
}

function handleConfirmOverwrite (files: Immutable.List<UploadedFile>) {
  FontsActions.uploadFonts(files, true);
}

function handleCancelOverwrite (files: Immutable.List<UploadedFile>) {
  FontsActions.cancelOverwrite(files);
}

function calculateState (prevState?: State, props?: Props): State {
  assertNonNullish(props);

  return {
    language: SettingsStore.getLanguage(),
    fonts: FontsStore.getItems(),
    selection: FontsStore.getSelection(),
    sortOrder: FontsStore.getSortOrder(),
    filter: FontsStore.getFilterString(),
    filterKeywords: FontsStore.getFilterKeywords(),
    index: props.index,
    activeTab: props.activeTab,
    allowMultiSelect: FontsStore.getAllowMultiSelect(),
    fontExtensionsString: FONT_EXTENSIONS_STRING,
    needsConfirmOverwrite: FontsStore.getState().extraData!.needsConfirmOverwrite,
    uploadedFiles: FontsStore.getState().extraData!.uploadedFiles,
    onDropFiles: handleDropFiles,
    setVisibleRange: FontsActions.setVisibleRange,
    onSortOrderChange: FontsActions.setSortOrder,
    setFilter: handleSetFilter,
    onRefresh: FontsActions.refresh,
    onSelectionChanged: FontsActions.setSelection,
    onAllowMultipleSelections: FontsActions.allowMultipleSelections,
    onItemSelect: FontsActions.modifySelection,
    onRemove: handleRemove,
    onRemoveMulti: handleRemoveMulti,
    onConfirmOverwrite: handleConfirmOverwrite,
    onCancelOverwrite: handleCancelOverwrite
  };
}

export default FluxUtils.Container.createFunctional(
  asFunctional(FontsTab),
  getStores,
  calculateState,
  { withProps: true }
);
